<template lang="pug">
tl-position(
    v-if="itemsLength > 0"
    class="tl-pagination"
    :x="!hideItemsPerPageInfo ? 'stretch' : 'center'"
    y="center"
)
    tl-position(
        class="tl-pagination-numbers"
        x="center"
        y="center"
    )
        template(
            v-if="pagination.length"
        )
            tl-button(
                :disabled="disabled || firstPage"
                icon
                opaq
                small
                color="gray"
                outlined
                data-testid="tl-pagination-prev"
                @click="prev"
            )
                i(class="mi-arrow-left")

            template(
                v-for="(page, index) in pagination"
                :key="index"
            )
                tl-button(
                    v-if="typeof page === 'number'"
                    icon
                    small
                    :color="page !== modelValue ? 'gray' : 'navy'"
                    outlined
                    :disabled="disabled"
                    :data-testid="`page-${ page }-button`"
                    @click="go(page)"
                )
                    p(
                        class="text-uppercase weight-semibold tl-pagination-number"
                    ) {{ page }}

                tl-input-dropdown(
                    v-else
                    :model-value="modelValue"
                    :items="allPages"
                    :dropdown-width="100"
                    dense
                    data-testid="page-dropdown"
                    @update:model-value="go($event)"
                )
                    template(#default="{ isFocused }")
                        tl-button(
                            icon
                            small
                            :opaq="!isFocused"
                            :disabled="disabled"
                            color="gray"
                            outlined
                        )
                            p(
                                class="text-uppercase weight-semibold tl-pagination-number"
                                data-testid="page-dots"
                            ) ...

            tl-button(
                :disabled="disabled || lastPage"
                icon
                opaq
                small
                color="gray"
                outlined
                data-testid="tl-pagination-next"
                @click="next"
            )
                i(class="mi-arrow-right")

    div(
        v-if="!hideItemsPerPageInfo"
        class="tl-pagination-info"
    )
        tl-position(
            v-if="perPageControl"
            x="center"
            y="center"
        )
            p Showing
            tl-input-dropdown(
                :model-value="itemsPerPage"
                :items="itemPerPageOptions"
                dense
                data-testid="per-page-dropdown"
                @update:model-value="updateItemsPerPage"
            )
            p
                | of
                span(data-testid="items-total-count")
                |  {{ itemsLength }}
                |  items
        p(
            v-else
        ) Showing
            span(data-testid="items-per-page") {{ itemsPerPage }}
            |  of
            span(data-testid="items-total-count") {{ itemsLength }}
            |  items
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

const props = withDefaults(defineProps<{
    modelValue: number
    itemsLength: number
    itemsPerPage?: number
    perPageControl?: boolean
    disabled?: boolean
    hideItemsPerPageInfo?: boolean
}>(), {
    itemsPerPage: 20,
    perPageControl: false,
    disabled: false,
    hideItemsPerPageInfo: false,
})

const emit = defineEmits<{
    (e: 'update:model-value', value: number): void
    (e: 'update:items-per-page', value: number): void
}>()

const itemsPerPage = ref(props.itemsPerPage)
const firstPage = computed(() => props.modelValue === 1)
const paginationLength = computed(() => {
    return Math.ceil(props.itemsLength / itemsPerPage.value)
})
const lastPage = computed(() => props.modelValue === paginationLength.value)

const MAX_PAGES_BEFORE_CURR = 1
const MAX_PAGES_AFTER_CURR = 2
const pagesToShow = 4

const allPages = computed(() => Array.from(
    Array(paginationLength.value + 2).keys(),
).slice(1, -1))

const pagination = computed(() => {
    if (paginationLength.value === 1) {
        return []
    }

    const maxPage = paginationLength.value
    const currentPage = props.modelValue
    let startPage = 1
    let endPage = maxPage

    if (currentPage <= pagesToShow) {
        startPage = 1
        endPage = pagesToShow + MAX_PAGES_AFTER_CURR
    } else if (currentPage + MAX_PAGES_AFTER_CURR >= maxPage) {
        startPage = maxPage - (pagesToShow + 1)
    } else {
        startPage = currentPage - MAX_PAGES_BEFORE_CURR
        endPage = currentPage + MAX_PAGES_AFTER_CURR
    }

    let pageNumbers: (string | number)[] = Array.from(Array(endPage + 1 - startPage).keys())
        .map((pageNumber) => startPage + pageNumber)
        .filter((pageNumber) => pageNumber <= maxPage && pageNumber > 0)

    const fPage = Number(pageNumbers[0])
    if (fPage > 1) {
        if (fPage <= 2) {
            pageNumbers = [1, ...pageNumbers]
        } else {
            const ellipsis = fPage > 3 ? '...' : 2
            pageNumbers = [1, ellipsis, ...pageNumbers]
        }
    }

    if (pageNumbers[pageNumbers.length - 1] !== maxPage) {
        if (pageNumbers[pageNumbers.length - 1] === maxPage - 1) {
            pageNumbers = [...pageNumbers, maxPage]
        } else {
            const ellipsis = Number(pageNumbers[pageNumbers.length - 1]) < maxPage - 2
                ? '...'
                : maxPage - 1

            pageNumbers = [...pageNumbers, ellipsis, maxPage]
        }
    }

    return pageNumbers
})

const next = () => {
    if (lastPage.value) return
    emit('update:model-value', props.modelValue + 1)
}

const prev = () => {
    if (firstPage.value) return
    emit('update:model-value', props.modelValue - 1)
}

const go = (value: number) => {
    emit('update:model-value', value)
}

const updateItemsPerPage = (value: number) => {
    itemsPerPage.value = value
    emit('update:items-per-page', value)
}

const itemPerPageOptions = [10, 20, 50, 100, 200]

watch(() => props.itemsPerPage, (value: number) => {
    if (value && value !== itemsPerPage.value) {
        updateItemsPerPage(value)
    }
})
</script>

<style lang="sass">
.tl-pagination
    .tl-button
        margin: 0 5px
        background-color: #fff!important
    &-number
        font-size: 14px
        line-height: 1.2em
        font-family: var(--font-family-secondary)
    .tl-button-text
        display: flex
        align-items: center
    &-numbers
        .tl-input-dropdown
            height: 30px
            .label-input-wrapper
                border: none
                padding: 0
            .arrow
                display: none!important

    &-info
        color: rgba(0, 0, 0, 0.7)
        .tl-input-dropdown
            margin-right: 12px
            margin-left: 12px
            .label-input-wrapper
                overflow: visible
                background-color: var(--color-white)

</style>
